import { Component } from 'react';
import { findDOMNode } from 'react-dom';
import styled from 'styled-components';
import { DOWN_KEY, ENTER_KEY, ESCAPE_KEY, UP_KEY } from '../../utils/keys';
import { ClickAwayListener } from '../ClickAwayListener/ClickAwayListener';
import { DefaultTheme } from '../themes/DefaultTheme';

const MenuElement = styled.ul`
  margin: 0;
  padding: 0;
  margin-top: 5px;
  border: 1px solid rgba(0, 0, 0, 0);
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.3);
  max-height: ${({ menuHeight }: any) => menuHeight}px;

  border-radius: 5px;
  left: 2px;
  right: 2px;
  top: ${({ menuPlacement, menuHeight }: any) => (menuPlacement === 'above' ? -(menuHeight + 10) : 50)}px;
  overflow: auto;
  position: absolute;
  z-index: 2;
  background-color: white;
  text-decoration: none;
  margin-bottom: 30px;
` as any;

const MenuItemElement = styled.li`
  margin: 0;
  cursor: pointer;
  display: block;
  border: none;
  height: auto;
  text-align: left;
  border-top: none;
  line-height: 1em;
  font-size: 16px;
  text-transform: none;
  box-shadow: none;
  padding: 0.8rem 1.1rem;
  box-sizing: border-box;
  white-space: normal;
  word-wrap: normal;
  text-decoration: none;
  color: ${({ theme }) => theme.vars.newColor.darkGS1};
  font-weight: 400;
  background-color: ${(props: any) => (props.isHighlighted ? DefaultTheme.dropdown.menuItem.hoverBgColor : 'white')};

  &:focus {
    outline: 0;
  }
` as any;

export interface ISelectItem<T> {
  id: T;
  label: string;
}

export type MenuPlacement = 'above' | 'below';

export interface IProps<T> {
  /** True if the menu is visible. Defaults to false. */
  showMenu: boolean;
  /** The list of items to select from. {id:string, label:string}. */
  items: Array<ISelectItem<T>>;
  /** The id of the currently selected menu item. */
  selectedMenuItemId: T;
  /** The placement of the menu.  One of "above" or "below" (default). */
  menuPlacement?: MenuPlacement;
  /** Height of the menu in pixels. Defaults to 250. */
  menuHeight?: number;
  /** Event fired when menu is closed. */
  onCloseMenu?: () => void;
  /** Event fired when a new value is selected. */
  onMenuItemSelected?: (value: T) => void;
}

export interface IState {
  highlightedIndex: number;
}

export class Menu<T = string> extends Component<IProps<T>, IState> {
  public static defaultProps: Pick<IProps<string>, 'menuHeight'> = {
    menuHeight: 200,
  };
  public state: IState = {
    highlightedIndex: 0,
  };
  private menuItemRefs: any[] = [];

  public render() {
    const { items } = this.props;
    return (
      <ClickAwayListener onClickAway={this.handleClickAway}>
        <MenuElement
          onKeyDown={this.handleKeyDown}
          menuHeight={this.props.menuHeight}
          menuPlacement={this.props.menuPlacement}
        >
          {items.map((item, index) => (
            <MenuItemElement
              tabIndex={-1}
              ref={el => (this.menuItemRefs[index] = el)}
              isHighlighted={index === this.state.highlightedIndex}
              key={index}
              onClick={this.handleMenuItemClicked.bind(this, item)}
              onMouseEnter={this.handleMouseEnteredItem.bind(this, item, index)}
            >
              {item.label}
            </MenuItemElement>
          ))}
        </MenuElement>
      </ClickAwayListener>
    );
  }

  public focusMenuItemById = menuItemId => {
    let highlightedIndex = this.props.items.findIndex(i => i.id === menuItemId);
    if (highlightedIndex === -1) {
      highlightedIndex = 0;
    }
    this.setState({ highlightedIndex }, () => {
      this.focusMenuItem(highlightedIndex);
    });
  };

  public focusMenuItem = highlightedIndex => {
    // eslint-disable-next-line react/no-find-dom-node
    const el: HTMLElement = findDOMNode(this.menuItemRefs[highlightedIndex]) as HTMLElement;

    if (el != null) {
      el.focus();
    }
  };

  private handleClickAway = e => {
    this.closeMenu();
  };

  private closeMenu = () => {
    if (this.props.onCloseMenu) {
      this.props.onCloseMenu();
    }
  };

  private handleMenuItemClicked = item => {
    this.setSelectedItem(item);
  };

  private setSelectedItem(selectedItem) {
    if (this.props.onMenuItemSelected) {
      this.props.onMenuItemSelected(selectedItem.id);
    }
  }

  private handleMouseEnteredItem = (_, highlightedIndex) => {
    this.setState({ highlightedIndex });
  };

  private handleKeyDown = e => {
    switch (e.keyCode) {
      case UP_KEY: {
        e.stopPropagation();
        e.preventDefault();

        const highlightedIndex =
          this.state.highlightedIndex > 0 ? this.state.highlightedIndex - 1 : this.state.highlightedIndex;
        this.setState({ highlightedIndex });
        this.focusMenuItem(highlightedIndex);
        break;
      }
      case DOWN_KEY: {
        e.stopPropagation();
        e.preventDefault();
        const highlightedIndex =
          this.state.highlightedIndex < this.props.items.length - 1
            ? this.state.highlightedIndex + 1
            : this.state.highlightedIndex;
        this.setState({ highlightedIndex });
        this.focusMenuItem(highlightedIndex);
        break;
      }
      case ENTER_KEY: {
        e.preventDefault();
        this.setSelectedItem(this.props.items[this.state.highlightedIndex]);
        break;
      }
      case ESCAPE_KEY: {
        e.preventDefault();
        this.closeMenu();
      }
    }
  };
}
